<template>
    <div>
        <div id="AmazonPayButton"></div>
        <v-btn v-if="showPlaceOrderButton" @click="createOrder" :loading="paymentLoading">
            {{ 'Pay with Amazon' | translation }}
        </v-btn>
    </div>
</template>

<script>
import module from './store/amazonPay';
import { AmazonPaymentType } from './config/amazon-payment-type-config';
import { mapState, mapMutations, mapActions } from 'vuex';
import { eventManager } from '@breakmedia/prime-vue-event-manager';
import Vue from 'vue';

const moduleName = 'AmazonPaymentType';

export default {
    name: moduleName,
    props: {
        onBasket: Boolean,
        shippingAddress: Object,
    },
    data() {
        return {
            showPlaceOrderButton: false,
            checkoutSessionId: null,
            paymentLoading: false,
            widgetStyle: {
                'min-width': '300px',
                width: '100%',
                'min-height': '228px',
                height: '240px',
                'max-height': '400px',
            },
            displayAddressBook: false,
            displayWallet: false,
            displayLogoutButton: false,
            displayWidgetsDialog: false,
            address: {},
            email: null,
        };
    },
    created() {
        let query = this.$route.query;
        if (query.transactionId && query.amazonCheckoutSessionId && query.orderId) {
            this.doPayment(query.transactionId, query.orderId, query.amazonCheckoutSessionId);
        } else if (this.paymentCompleted && query.transactionId) {
            this.$nextTick(() => {
                this.$emit('payment-success', { transaction_id: query.transactionId });
            });
        } else if (query.authorized && query.amazonCheckoutSessionId) {
            this.showAuthorized(query.amazonCheckoutSessionId);
        } else {
            this.loadPaymentConfiguration().then(() => {
                if (!document.getElementById('amazonPayScript')) {
                    const script = document.createElement('script');
                    script.src = 'https://static-eu.payments-amazon.com/checkout.js';
                    script.id = 'amazonPayScript';
                    script.async = true;
                    document.body.appendChild(script);

                    script.onload = this.initAmazonPayButton;
                } else {
                    this.initAmazonPayButton();
                }
            });
        }
    },
    beforeDestroy() {
        delete window.onAmazonLoginReady;
    },
    computed: {
        ...mapState(moduleName, ['config', 'customerAddress', 'paymentProcessing', 'paymentCompleted']),
    },
    methods: {
        ...mapMutations(moduleName, ['setError', 'setPaymentProcessing']),
        ...mapActions(moduleName, [
            'loadPaymentConfiguration',
            'createCheckoutSession',
            'createBasketCheckoutSession',
            'executePayment',
            'setPaymentInfo',
            'getAmazonAddressInfo',
        ]),
        ...mapMutations('Payment', ['setPaymentSummary']),
        showAuthorized(amazonCheckoutSessionId) {
            console.log('authorized');
            this.setPaymentSummary(true);
            this.getAmazonAddressInfo({ amazonCheckoutSessionId })
                .then(async result => {
                    if (!result.error) {
                        const address = this.convertAddress(result);
                        console.log(result, address);
                        this.$store.commit('BillingAddressForm/setAddress', address);
                        this.$store.commit('ShippingAddressForm/setAddress', address);
                        this.address = address;
                        this.email = result.buyer.email;
                        this.showPlaceOrderButton = true;
                    } else {
                        console.error(result.error);
                        this.$router.push({
                            name: 'checkout',
                            params: {
                                stepName: 'address',
                            },
                        });
                    }
                })
                .catch(error => {
                    console.error(error);
                    this.$router.push({
                        name: 'checkout',
                        params: {
                            stepName: 'address',
                        },
                    });
                });
        },
        convertAddress({ buyer, shippingAddress }) {
            const [firstName, ...lastNameParts] = shippingAddress.name.split(' ');
            const lastName = lastNameParts.join(' ');

            return {
                city: shippingAddress.city,
                country: shippingAddress.countryCode,
                firstName: firstName || '',
                lastName: lastName || '',
                postCode: shippingAddress.postalCode,
                street: shippingAddress.addressLine1 || shippingAddress.addressLine2 || '',
                packingStation: null, // Amazon Pay does not return packing station details
                packingStationPostCode: null,
                fiscalCode: null, // Fiscal code is not provided by Amazon
                houseNumber: null, // House number extraction would require more logic
                email: buyer.email,
                phone: shippingAddress.phoneNumber || buyer.phoneNumber || '',
            };
        },
        initAmazonPayButton() {
            console.log(this.onBasket);
            if (this.onBasket) {
                this.createBasketCheckoutSession()
                    .then(({ payload, signature, publicKeyId }) => {
                        console.log(payload, signature, publicKeyId);
                        console.log(this.config);
                        amazon.Pay.renderButton('#AmazonPayButton', {
                            merchantId: this.config.merchantId,
                            ledgerCurrency: this.config.currencyCode,
                            sandbox: this.config.sandbox,
                            productType: 'PayAndShip',
                            placement: 'Cart',
                            buttonColor: this.config.buttonColor,
                            estimatedOrderAmount: {
                                amount: this.$basket.getBasketTotal(),
                                currencyCode: 'EUR',
                            },
                            createCheckoutSessionConfig: {
                                payloadJSON: payload,
                                signature,
                                algorithm: 'AMZN-PAY-RSASSA-PSS-V2',
                                publicKeyId,
                            },
                            onError: error => {
                                console.error('Amazon Pay Button Error:', error);
                                this.setError('Amazon Pay button initialization failed.');
                            },
                        });
                    })
                    .catch(error => {
                        console.error('Error initializing Amazon Pay button:', error);
                        this.setError('Failed to initialize Amazon Pay button.');
                    });
            } else {
                this.$order.getOrder().then(order => {
                    console.log(order, order.id, this.shippingAddress);
                    if (order && order.id) {
                        this.createCheckoutSession({
                            orderId: order.id,
                            transactionId: order.transaction_id,
                            shippingAddress: this.shippingAddress,
                        })
                            .then(({ payload, signature, publicKeyId }) => {
                                console.log(payload, signature, publicKeyId);
                                console.log(this.config);
                                amazon.Pay.renderButton('#AmazonPayButton', {
                                    merchantId: this.config.merchantId,
                                    ledgerCurrency: this.config.currencyCode,
                                    sandbox: this.config.sandbox,
                                    productType: 'PayAndShip',
                                    placement: 'Checkout',
                                    buttonColor: this.config.buttonColor,
                                    createCheckoutSessionConfig: {
                                        payloadJSON: payload,
                                        signature,
                                        algorithm: 'AMZN-PAY-RSASSA-PSS-V2',
                                        publicKeyId,
                                    },
                                    onError: error => {
                                        console.error('Amazon Pay Button Error:', error);
                                        this.setError('Amazon Pay button initialization failed.');
                                    },
                                });
                            })
                            .catch(error => {
                                console.error('Error initializing Amazon Pay button:', error);
                                this.setError('Failed to initialize Amazon Pay button.');
                            });
                    }
                });
            }
        },

        doPayment(transactionId, orderId, amazonCheckoutSessionId) {
            console.log('Do payment');
            if (this.paymentProcessing) {
                return true;
            }
            this.setPaymentProcessing(true);
            this.executePayment({ orderId, transactionId, amazonCheckoutSessionId })
                .then(result => {
                    if (result && result.success) {
                        this.setPaymentProcessing(false);
                        console.log('Payment completed');
                        this.onPaymentSuccess({
                            transaction_id: transactionId,
                            paid: true,
                        });
                    } else {
                        this.setPaymentProcessing(false);
                        this.$emit('cancel', { transaction_id: transactionId });
                    }
                })
                .catch(() => {
                    this.setPaymentProcessing(false);
                    this.$emit('cancel', { transaction_id: transactionId });
                });
        },

        async onPaymentSuccess(orderData) {
            await this.$router.push({
                name: 'checkout',
                params: {
                    stepName: 'success',
                },
                query: {
                    transaction_id: orderData.transaction_id,
                },
            });
            console.log('onPaymentSuccess', orderData);

            return Vue.$order
                .getOrderByFilters({
                    transactionId: orderData.transaction_id,
                    include_paid: true,
                })
                .then(order => {
                    this.order = order;
                    this.$basket.deleteBasket();

                    if (orderData.paid === false) {
                        this.orderIsPaid = false;
                    }

                    eventManager.$emit('purchase', {
                        order,
                        eventId: eventManager.getUniqueId('purchase'),
                    });
                });
        },

        createOrder() {
            let query = this.$route.query;
            console.log(query.authorized, query.amazonCheckoutSessionId);
            this.$order.getOrder(true, this.address, this.email).then(order => {
                console.log(order);
                if (order && order.id) {
                    this.setPaymentInfo({
                        orderId: order.id,
                        transactionId: order.transaction_id,
                        amazonCheckoutSessionId: query.amazonCheckoutSessionId,
                    })
                        .then(result => {
                            if (!result.error) {
                                window.location.href = result.amazonPayRedirectUrl;
                            } else {
                                console.error(result.error);
                            }
                        })
                        .catch(error => {
                            console.log(error);
                        });
                }
            });
        },
    },
    install(Vue) {
        if (!Vue.$store.state[moduleName]) {
            Vue.$store.registerModule(moduleName, module);
        }
        Vue.component(moduleName, this);
        Vue.$store.commit('Payment/addPaymentType', new AmazonPaymentType());
    },
};
</script>

<style>
#AmazonPayButton {
    float: right;
}

#addressBookWidgetDiv iframe {
    z-index: auto !important;
}

#walletWidgetDiv iframe {
    z-index: auto !important;
}
</style>
